import { useState, useMemo } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { KeyRound } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = useMemo(() => searchParams.get('token') || '', [searchParams]);

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token) {
      toast.error('Missing reset link. Request a new one from the login page.');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: newPassword });
      setDone(true);
      toast.success('Password reset successfully! You can now log in.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 p-6">
      <Card className="w-full max-w-md bg-slate-900 border-slate-800 p-8" data-testid="reset-password-page">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center">
            <KeyRound className="w-5 h-5 text-orange-400" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-white">Reset password</h1>
            <p className="text-sm text-slate-400">Choose a new password for your account</p>
          </div>
        </div>

        {!token && (
          <div className="text-sm text-red-400 mb-4" data-testid="reset-missing-token">
            This reset link is missing a token. Request a new reset email from the login page.
          </div>
        )}

        {done ? (
          <div className="space-y-4">
            <p className="text-slate-300 text-sm" data-testid="reset-success-message">
              Your password has been updated. You can now sign in with your new password.
            </p>
            <Button
              className="w-full bg-orange-500 hover:bg-orange-600"
              onClick={() => navigate('/login')}
              data-testid="reset-goto-login"
            >
              Go to login
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="new-password" className="text-slate-200">New password</Label>
              <Input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={6}
                className="bg-slate-800 border-slate-700 text-white"
                data-testid="reset-new-password-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="confirm-password" className="text-slate-200">Confirm new password</Label>
              <Input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={6}
                className="bg-slate-800 border-slate-700 text-white"
                data-testid="reset-confirm-password-input"
              />
            </div>
            <Button
              type="submit"
              disabled={loading || !token}
              className="w-full bg-orange-500 hover:bg-orange-600"
              data-testid="reset-submit-btn"
            >
              {loading ? 'Resetting…' : 'Reset password'}
            </Button>
            <div className="text-center text-sm text-slate-400">
              <Link to="/login" className="hover:text-orange-400" data-testid="reset-back-to-login">Back to login</Link>
            </div>
          </form>
        )}
      </Card>
    </div>
  );
}
